<script setup lang="ts">
  import {
    SelectContent,
    type SelectContentEmits,
    type SelectContentProps,
    SelectPortal,
    SelectViewport,
    injectSelectRootContext,
    useForwardPropsEmits
  } from 'reka-ui'
  import { computed, type HTMLAttributes } from 'vue'
  import { cn } from '../../../lib/utils'
  import { useDialogZIndex } from '../../../composables/useDialogZIndex'
  import SelectScrollDownButton from './SelectScrollDownButton.vue'
  import SelectScrollUpButton from './SelectScrollUpButton.vue'

  defineOptions({
    inheritAttrs: false
  })

  const props = withDefaults(
    defineProps<SelectContentProps & { class?: HTMLAttributes['class'] }>(),
    {
      position: 'popper'
    }
  )
  const emits = defineEmits<SelectContentEmits>()

  const delegatedProps = computed(() => {
    const { class: _, ...delegated } = props

    return delegated
  })

  const forwarded = useForwardPropsEmits(delegatedProps, emits)

  // Like the other overlays (Popover/Combobox/Dropdown), derive the z from the
  // select's open state so it sits at the host modal's z and isn't hidden
  // behind a stacked modal. A hardcoded `z-50` fell below every modal (z>=51).
  const rootContext = injectSelectRootContext()
  const open = computed(() => rootContext.open.value)
  const { zIndex } = useDialogZIndex(open)
</script>

<template>
  <SelectPortal>
    <SelectContent
      data-slot="select-content"
      v-bind="{ ...forwarded, ...$attrs }"
      :style="{ zIndex }"
      :class="
        cn(
          'bg-popover text-popover-foreground max-h-(--reka-select-content-available-height) relative min-w-[8rem] overflow-y-auto overflow-x-hidden rounded-md border shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          position === 'popper' &&
            'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
          props.class
        )
      "
    >
      <SelectScrollUpButton />
      <SelectViewport
        :class="
          cn(
            'p-1',
            position === 'popper' &&
              'h-[var(--reka-select-trigger-height)] w-full min-w-[var(--reka-select-trigger-width)] scroll-my-1'
          )
        "
      >
        <slot />
      </SelectViewport>
      <SelectScrollDownButton />
    </SelectContent>
  </SelectPortal>
</template>
